import React, { useState, useEffect, useRef } from 'react';
import { PropertyDetails, EstimationResult } from '../types';
import { estimationService } from '../services/estimationService';
import { Loader2, TrendingUp, CheckCircle, Sparkles, Minus, Plus, Building, Home, MapPin, Phone, Mail, Edit2 } from 'lucide-react';
import { initPostHog, posthogService } from '../lib/posthog';

// Declare grecaptcha for TypeScript
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

interface CounterProps { 
  label: string; 
  value: number; 
  field: keyof PropertyDetails; 
  min?: number; 
  suffix?: string;
  step?: number;
  onIncrement: (field: keyof PropertyDetails, step?: number) => void;
  onDecrement: (field: keyof PropertyDetails, min?: number, step?: number) => void;
  onManualInput: (e: React.ChangeEvent<HTMLInputElement>, field: keyof PropertyDetails) => void;
}

const Counter: React.FC<CounterProps> = ({ 
  label, 
  value, 
  field, 
  min = 0, 
  suffix = '', 
  step = 1,
  onIncrement,
  onDecrement,
  onManualInput
}) => (
  <div className="flex flex-col">
    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">{label}</label>
    <div className="flex items-center bg-gray-50 rounded-lg p-1 border border-gray-200">
      <button 
        type="button"
        onClick={() => onDecrement(field, min, step)}
        className="w-10 h-10 flex items-center justify-center bg-white rounded-md shadow-sm text-gray-600 hover:text-amber-600 transition-colors hover:shadow active:scale-95 border border-gray-100 touch-manipulation"
      >
        <Minus size={16} />
      </button>
      <div className="flex-1 text-center font-semibold text-lg text-gray-900 flex items-center justify-center">
          <input 
              type="number" 
              value={value} 
              onChange={(e) => onManualInput(e, field)}
              onFocus={(e) => e.target.select()}
              className="w-16 text-center bg-transparent outline-none appearance-none m-0 p-0 text-base"
          />
          {suffix && <span className="text-gray-400 text-sm ml-1">{suffix}</span>}
      </div>
      <button 
        type="button"
        onClick={() => onIncrement(field, step)}
        className="w-10 h-10 flex items-center justify-center bg-white rounded-md shadow-sm text-gray-600 hover:text-amber-600 transition-colors hover:shadow active:scale-95 border border-gray-100 touch-manipulation"
      >
        <Plus size={16} />
      </button>
    </div>
  </div>
);

export const EstimationIframe: React.FC = () => {
  const [step, setStep] = useState<'form' | 'loading' | 'result' | 'error'>('form');
  const [formData, setFormData] = useState<PropertyDetails>({
    address: '',
    propertyType: 'apartment',
    bedrooms: 1,
    bathrooms: 1,
    area: 30,
    email: '',
    phone: ''
  });
  const [result, setResult] = useState<EstimationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // URL Parameters
  const [urlParams, setUrlParams] = useState<{
    providerId?: string;
    source?: string;
    campaign?: string;
    bg?: string;
  }>({});

  // reCAPTCHA state
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const GTM_ID = 'GTM-T2PT7H6';

  // Address Autocomplete State
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Initialize PostHog and extract URL parameters on mount
  useEffect(() => {
    // Initialize PostHog first
    initPostHog();

    const params = new URLSearchParams(window.location.search);
    const providerId = params.get('providerId');
    const source = params.get('source');
    const campaign = params.get('campaign');
    const bg = params.get('bg');

    if (!providerId) {
      setStep('error');
      setErrorMessage('Paramètre providerId manquant dans l\'URL');
      return;
    }

    setUrlParams({
      providerId,
      source: source || undefined,
      campaign: campaign || undefined,
      bg: bg || undefined,
    });

    // Enregistrer les propriétés de session pour tous les événements
    posthogService.register({
      providerId: providerId || 'unknown',
      source: source || 'unknown',
      campaign: campaign || 'unknown',
      isDirect: providerId === 'direct'
    });

    // Track page view
    posthogService.track('estimation_page_view', {
      providerId,
      source,
      campaign,
      isDirect: providerId === 'direct',
      timestamp: new Date().toISOString()
    });
  }, []);

  // Initialize GTM only on public estimator route
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    const scriptSelector = `script[src="https://www.googletagmanager.com/gtm.js?id=${GTM_ID}"]`;
    const existingScript = document.querySelector(scriptSelector);
    if (existingScript) return;

    const firstScript = document.getElementsByTagName('script')[0];
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(gtmScript, firstScript);
    } else {
      document.head.appendChild(gtmScript);
    }
  }, [GTM_ID]);

  // Set body and html background based on URL parameter
  useEffect(() => {
    // Save original styles
    const originalBodyBackground = document.body.style.backgroundColor;
    const originalHtmlBackground = document.documentElement.style.backgroundColor;
    const originalBodyHeight = document.body.style.height;
    const originalHtmlHeight = document.documentElement.style.height;
    const originalBgClass = document.body.className;
    
    // Determine background color based on bg parameter
    let backgroundColor = 'transparent';
    if (urlParams.bg === 'placid') {
      backgroundColor = '#14213C';
      // Set height to auto so body takes only the size of its content
      document.documentElement.style.height = 'auto';
      document.body.style.height = 'auto';
    } else {
      // Reset height when bg is not placid
      document.documentElement.style.height = originalHtmlHeight;
      document.body.style.height = originalBodyHeight;
    }
    
    // Apply background to both html and body
    document.documentElement.style.backgroundColor = backgroundColor;
    document.body.style.backgroundColor = backgroundColor;
    
    // Cleanup: restore original styles on unmount
    return () => {
      document.documentElement.style.backgroundColor = originalHtmlBackground;
      document.documentElement.style.height = originalHtmlHeight;
      document.body.style.backgroundColor = originalBodyBackground;
      document.body.style.height = originalBodyHeight;
      document.body.className = originalBgClass;
    };
  }, [urlParams.bg]);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('VITE_RECAPTCHA_SITE_KEY not configured');
      setRecaptchaError('Configuration reCAPTCHA manquante');
      return;
    }

    // Check if already loaded
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }

    // Load script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true);
      });
    };
    
    script.onerror = () => {
      setRecaptchaError('Erreur lors du chargement de reCAPTCHA');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script if component unmounts
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript) {
        // Don't remove if other components might use it
      }
    };
  }, [RECAPTCHA_SITE_KEY]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleAddressChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, address: value }));

    // Track address input
    if (value.length > 3) {
      posthogService.track('estimation_address_input', {
        addressLength: value.length
      });
    }

    if (value.length > 3) {
      try {
        const response = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(value)}&limit=5&lat=45.772&lon=4.886`);
        const data = await response.json();
        setSuggestions(data.features || []);
        setShowSuggestions(true);
        
        // Track suggestions shown
        if (data.features && data.features.length > 0) {
          posthogService.track('estimation_address_suggestions_shown', {
            suggestionsCount: data.features.length
          });
        }
      } catch (err) {
        console.error("Address API error", err);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectAddress = (feature: any) => {
    // Utiliser label pour l'affichage (adresse complète formatée)
    // Utiliser name (rue), postcode et city séparément pour l'envoi au backend
    setFormData(prev => ({ 
      ...prev, 
      address: feature.properties.label, // Adresse complète pour l'affichage
      street: feature.properties.name, // Rue uniquement pour l'envoi au backend
      city: feature.properties.city,
      zipCode: feature.properties.postcode
    }));
    setShowSuggestions(false);
    
    // Track address selection
    posthogService.track('estimation_address_selected', {
      address: feature.properties.label,
      street: feature.properties.name,
      city: feature.properties.city,
      zipCode: feature.properties.postcode
    });
  };

  const clearAddress = () => {
    setFormData(prev => ({
      ...prev,
      address: '',
      street: undefined,
      city: undefined,
      zipCode: undefined
    }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const isAddressSelected = !!(formData.street && formData.city && formData.zipCode);

  const handleIncrement = (field: keyof PropertyDetails, step = 1) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as number) + step
    }));
    
    // Track field increment
    posthogService.track('estimation_field_increment', {
      field,
      newValue: (formData[field] as number) + step
    });
  };

  const handleDecrement = (field: keyof PropertyDetails, min = 0, step = 1) => {
    setFormData(prev => ({
      ...prev,
      [field]: Math.max(min, (prev[field] as number) - step)
    }));
    
    // Track field decrement
    posthogService.track('estimation_field_decrement', {
      field,
      newValue: Math.max(min, (formData[field] as number) - step)
    });
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>, field: keyof PropertyDetails) => {
    const val = field === 'email' || field === 'phone' || field === 'address' 
      ? e.target.value 
      : parseInt(e.target.value);

    const finalVal = typeof val === 'number' && isNaN(val) ? 0 : val;

    setFormData(prev => ({
      ...prev,
      [field]: finalVal
    }));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    window.dataLayer?.push({ event: 'formulaire_simulateur_soumis' });
    
    // Track form submission attempt
    posthogService.track('estimation_form_submit_attempt', {
      hasAddress: !!formData.address,
      hasEmail: !!formData.email,
      hasPhone: !!formData.phone,
      propertyType: formData.propertyType,
      area: formData.area,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms
    });

    if (!formData.address || !formData.email || !formData.phone) {
      setErrorMessage("Merci de renseigner l'adresse, l'email et le téléphone pour recevoir votre étude.");
      
      // Track validation error
      posthogService.track('estimation_form_validation_error', {
        missingFields: {
          address: !formData.address,
          email: !formData.email,
          phone: !formData.phone
        }
      });
      return;
    }

    if (!urlParams.providerId) {
      setStep('error');
      setErrorMessage('Configuration invalide');
      return;
    }

    if (!recaptchaLoaded) {
      setErrorMessage('reCAPTCHA en cours de chargement. Veuillez patienter...');
      return;
    }

    if (recaptchaError) {
      setErrorMessage('Erreur reCAPTCHA. Veuillez rafraîchir la page.');
      return;
    }

    setStep('loading');
    setErrorMessage('');

    // Track form submission success
    posthogService.track('estimation_form_submitted', {
      propertyType: formData.propertyType,
      area: formData.area,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      hasAddress: !!formData.address
    });

    try {
      // Execute reCAPTCHA
      let recaptchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY && window.grecaptcha) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
            action: 'estimation_submit'
          });
        } catch (recaptchaErr) {
          console.error('reCAPTCHA execution error:', recaptchaErr);
          setStep('form');
          setErrorMessage('Erreur lors de la validation. Veuillez réessayer.');
          
          // Track reCAPTCHA error
          posthogService.track('estimation_recaptcha_error', {
            error: String(recaptchaErr)
          });
          return;
        }
      }

      const data = await estimationService.estimate({
        mode: 'public',
        propertyDetails: formData,
        recaptchaToken,
        providerId: urlParams.providerId,
        email: formData.email,
        phone: formData.phone,
        source: urlParams.source,
        campaign: urlParams.campaign,
      });

      setResult(data);
      setStep('result');

      // Track successful estimation
      posthogService.track('estimation_success', {
        isEligible: data.isEligible,
        lowEstimate: data.lowEstimate,
        highEstimate: data.highEstimate,
        occupancyRate: data.occupancyRate,
        averageNightlyPrice: data.averageNightlyPrice,
        propertyType: formData.propertyType,
        area: formData.area
      });

      // Track conversion (lead créé)
      if (data.isEligible) {
        posthogService.track('estimation_conversion', {
          providerId: urlParams.providerId,
          source: urlParams.source,
          campaign: urlParams.campaign,
          isDirect: urlParams.providerId === 'direct',
          estimatedRevenue: (data.lowEstimate + data.highEstimate) / 2
        });
      } else {
        // Track non-eligible
        posthogService.track('estimation_not_eligible', {
          providerId: urlParams.providerId,
          source: urlParams.source,
          campaign: urlParams.campaign
        });
      }

    } catch (error: any) {
      console.error(error);
      setStep('form');
      setErrorMessage("Une erreur est survenue lors de l'estimation. Veuillez réessayer plus tard.");
      
      // Track error
      posthogService.track('estimation_error', {
        errorMessage: error.message || 'Unknown error'
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
  };

  const COMMISSION_RATE = 0.25;

  // Track result view
  useEffect(() => {
    if (step === 'result' && result) {
      posthogService.track('estimation_result_viewed', {
        isEligible: result.isEligible,
        lowEstimate: result.lowEstimate,
        highEstimate: result.highEstimate
      });
    }
  }, [step, result]);

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-transparent">
        <div className="bg-white rounded-lg shadow-lg border border-red-200 p-8 max-w-md text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Erreur de configuration</h2>
          <p className="text-slate-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          
          {step === 'form' && (
            <div className="p-8 lg:p-12">
              <div className="text-center mb-10">
                <span className="inline-block py-1 px-3 rounded-full bg-amber-100 text-amber-700 text-xs font-bold tracking-widest uppercase mb-3">
                  Estimation Gratuite
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Combien votre bien peut-il rapporter ?</h2>
                <p className="text-gray-500 max-w-xl mx-auto">Notre intelligence artificielle analyse le marché en temps réel pour vous donner une estimation précise.</p>
              </div>

              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Address Section */}
                <div className="relative" ref={wrapperRef}>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Adresse du bien</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MapPin className={`h-5 w-5 transition-colors duration-300 ${isAddressSelected ? 'text-gray-400' : 'text-amber-600 group-focus-within:scale-110 transition-transform duration-300'}`} />
                    </div>
                    <input
                      type="text"
                      name="address"
                      required
                      autoComplete="off"
                      placeholder="Entrez votre adresse..."
                      disabled={isAddressSelected}
                      className={`block w-full pl-12 ${isAddressSelected ? 'pr-24' : 'pr-4'} py-4 rounded-xl transition-all duration-300 font-medium text-base ${
                        isAddressSelected 
                          ? 'bg-gray-100 border border-gray-300 text-gray-600 cursor-not-allowed' 
                          : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500'
                      }`}
                      value={formData.address}
                      onChange={handleAddressChange}
                    />
                    {isAddressSelected && (
                      <button
                        type="button"
                        onClick={clearAddress}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-amber-600 hover:text-amber-700 transition-colors duration-200"
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        <span className="text-sm font-medium">Modifier</span>
                      </button>
                    )}
                  </div>
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="absolute z-50 w-full bg-white border border-gray-100 mt-2 rounded-xl shadow-xl max-h-60 overflow-auto divide-y divide-gray-50 py-2">
                      {suggestions.map((feature, idx) => (
                        <li 
                          key={idx}
                          onClick={() => selectAddress(feature)}
                          className="px-5 py-3 hover:bg-amber-50 cursor-pointer text-sm text-gray-700 flex items-center transition-colors"
                        >
                          <MapPin size={14} className="mr-3 text-gray-400" />
                          {feature.properties.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Property Type Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Type de propriété</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      onClick={() => setFormData(prev => ({ ...prev, propertyType: 'apartment' }))}
                      className={`cursor-pointer border rounded-xl p-4 flex flex-col items-center justify-center transition-all duration-300 ${formData.propertyType === 'apartment' ? 'border-amber-600 bg-amber-50 text-amber-700 ring-1 ring-amber-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:bg-white'}`}
                    >
                      <Building className={`mb-2 h-6 w-6 ${formData.propertyType === 'apartment' ? 'fill-current' : ''}`} />
                      <span className="font-semibold text-sm">Appartement</span>
                    </div>
                    <div 
                      onClick={() => setFormData(prev => ({ ...prev, propertyType: 'house' }))}
                      className={`cursor-pointer border rounded-xl p-4 flex flex-col items-center justify-center transition-all duration-300 ${formData.propertyType === 'house' ? 'border-amber-600 bg-amber-50 text-amber-700 ring-1 ring-amber-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:bg-white'}`}
                    >
                      <Home className={`mb-2 h-6 w-6 ${formData.propertyType === 'house' ? 'fill-current' : ''}`} />
                      <span className="font-semibold text-sm">Maison</span>
                    </div>
                  </div>
                </div>

                {/* Counters Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Counter 
                    label="Surface" 
                    value={formData.area} 
                    field="area" 
                    min={9} 
                    suffix="m²" 
                    step={1} 
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onManualInput={handleManualInput}
                  />
                  <Counter 
                    label="Chambres" 
                    value={formData.bedrooms} 
                    field="bedrooms" 
                    min={0}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onManualInput={handleManualInput}
                  />
                  <Counter 
                    label="Salles de bain" 
                    value={formData.bathrooms} 
                    field="bathrooms" 
                    min={1} 
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onManualInput={handleManualInput}
                  />
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-gray-100">
                  <div>
                     <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email</label>
                     <div className="relative">
                       <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                         <Mail className="h-4 w-4 text-gray-400" />
                       </div>
                       <input
                         type="email"
                         required
                         placeholder="votre@email.com"
                         className="block w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-base"
                         value={formData.email}
                         onChange={(e) => handleManualInput(e, 'email')}
                       />
                     </div>
                  </div>
                  <div>
                     <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Téléphone</label>
                     <div className="relative">
                       <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                         <Phone className="h-4 w-4 text-gray-400" />
                       </div>
                       <input
                         type="tel"
                         required
                         placeholder="06 12 34 56 78"
                         className="block w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-base"
                         value={formData.phone}
                         onChange={(e) => handleManualInput(e, 'phone')}
                       />
                     </div>
                  </div>
                </div>

                {/* Action */}
                <div className="pt-4">
                  <button 
                    type="submit" 
                    disabled={!recaptchaLoaded || !!recaptchaError}
                    className="w-full h-16 text-lg rounded-xl shadow-xl shadow-amber-500/20 hover:shadow-2xl hover:shadow-amber-500/30 hover:-translate-y-0.5 transition-all bg-amber-600 text-white font-semibold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Calculer mon revenu potentiel <TrendingUp className="ml-2 h-5 w-5" />
                  </button>
                  <p className="text-center text-xs text-gray-400 mt-4">
                    En cliquant, vous acceptez d'être contacté pour affiner cette estimation.
                  </p>
                </div>
              </form>
            </div>
          )}

          {step === 'loading' && (
            <div className="p-20 flex flex-col items-center justify-center text-center">
              <div className="relative">
                  <div className="absolute inset-0 bg-amber-500 blur-xl opacity-20 rounded-full animate-pulse"></div>
                  <Loader2 className="h-16 w-16 text-amber-600 animate-spin relative z-10" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-2">Analyse de votre bien...</h3>
              <p className="text-gray-500 max-w-md">
                Nos algorithmes comparent votre propriété avec les tendances actuelles du marché.
              </p>
            </div>
          )}

          {step === 'result' && result && (
            <>
              {result.isEligible ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[500px]">
                  {/* Left: Key Stats */}
                  <div className="bg-slate-900 text-white p-10 flex flex-col justify-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500 rounded-full opacity-10 blur-3xl -mr-32 -mt-32"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full opacity-10 blur-3xl -ml-32 -mb-32"></div>
                    
                    <div className="relative z-10">
                      <h3 className="text-sm font-bold text-amber-500 uppercase tracking-widest mb-6">Potentiel Mensuel</h3>
                      <div className="flex items-baseline mb-2">
                          <span className="text-5xl md:text-6xl font-bold text-white tracking-tight">
                          {formatCurrency(result.highEstimate)}
                          </span>
                          <span className="text-xl text-gray-400 ml-2">*</span>
                      </div>
                      <p className="text-gray-300 mb-8 border-l-2 border-amber-500 pl-4 text-sm leading-relaxed opacity-80">
                          Estimation haute des revenus bruts basée sur une performance optimale de votre bien.
                      </p>
                      
                      <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm border border-white/10">
                          <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-300 font-medium">Taux d'occupation</span>
                          <span className="font-bold text-amber-500 text-lg">{result.occupancyRate}%</span>
                          </div>
                          <div className="w-full bg-gray-700 h-1.5 rounded-full overflow-hidden">
                              <div className="bg-amber-500 h-full rounded-full" style={{ width: `${result.occupancyRate}%` }}></div>
                          </div>
                          <p className="text-xs text-gray-400 mt-3 italic">Moyenne annuelle lissée</p>
                      </div>
                    </div>
                  </div>

                  {/* Right: Detailed Breakdown */}
                  <div className="lg:col-span-2 p-10 bg-white flex flex-col justify-between">
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                          <div>
                          <h3 className="text-2xl font-bold text-slate-900">Revenu net estimé</h3>
                          <p className="text-gray-500 text-sm mt-1">{result.description}</p>
                          </div>
                          <button 
                          onClick={() => setStep('form')}
                          className="text-gray-400 hover:text-slate-900 underline text-sm transition-colors"
                          >
                          Refaire une estimation
                          </button>
                      </div>

                      <div className="space-y-3">
                          <div className="flex justify-between items-center p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                          <span className="text-gray-600 font-medium">Revenus bruts (Moyenne)</span>
                          <span className="font-bold text-gray-800 text-lg">
                              {formatCurrency((result.lowEstimate + result.highEstimate) / 2)}
                          </span>
                          </div>

                          <div className="flex justify-between items-center p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                          <div className="flex flex-col">
                              <span className="text-gray-600 font-medium flex items-center gap-2">
                                  Ménage & Blanchisserie
                                  <Sparkles className="h-3.5 w-3.5 text-amber-600" />
                              </span>
                              <span className="text-xs text-gray-400">(Payé par les voyageurs)</span>
                          </div>
                          <span className="font-bold text-gray-400 text-sm">
                              0 € (Neutre)
                          </span>
                          </div>

                          <div className="flex justify-between items-center p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                          <div className="flex flex-col">
                              <span className="text-gray-600 font-medium">Commission (25%)</span>
                              <span className="text-xs text-gray-400">Gestion complète 100% déléguée</span>
                          </div>
                          <span className="font-bold text-red-400">
                              - {formatCurrency(((result.lowEstimate + result.highEstimate) / 2) * COMMISSION_RATE)}
                          </span>
                          </div>

                          <div className="flex justify-between items-center p-5 bg-green-50 border border-green-100 rounded-xl mt-4 relative overflow-hidden">
                              <div className="absolute inset-y-0 left-0 w-1 bg-green-500"></div>
                              <span className="text-slate-900 font-bold text-lg z-10">VOTRE NET / MOIS</span>
                              <span className="font-bold text-green-700 text-3xl z-10">
                                  {formatCurrency(((result.lowEstimate + result.highEstimate) / 2) * (1 - COMMISSION_RATE))}
                              </span>
                          </div>
                      </div>
                    </div>

                    <div className="mt-8">
                       <p className="text-[10px] text-gray-400 text-center mb-6 max-w-lg mx-auto leading-tight">
                          * Estimation non contractuelle fournie à titre indicatif. Une visite technique est nécessaire pour valider ce montant.
                      </p>
                      
                      <div className="flex flex-col gap-4">
                          <button 
                               className="flex items-center justify-center w-full rounded-xl h-12 text-sm font-medium border-2 border-gray-200 text-gray-600 hover:border-slate-900 hover:text-slate-900 transition-all"
                          >
                               <CheckCircle className="mr-2 h-4 w-4" />
                               Demande de contact envoyée
                          </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-16 text-center bg-gray-50/50">
                  <div className="bg-white p-6 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-sm">
                      <Sparkles className="h-10 w-10 text-amber-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Besoin d'une expertise approfondie</h3>
                  <p className="text-gray-600 max-w-xl mx-auto mb-10 leading-relaxed">
                     Le simulateur ne peut pas estimer automatiquement les biens situés dans cette zone car la réglementation ou le marché nécessitent une analyse humaine.
                     <br/><br/>
                     Pas d'inquiétude : un expert peut évaluer précisément votre potentiel locatif.
                  </p>
                  <button 
                      onClick={() => setStep('form')}
                      className="mt-8 text-gray-400 hover:text-slate-900 underline text-sm transition-colors"
                  >
                      Nouvelle recherche
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
